import { defineComponent as _defineComponent } from 'vue'
import { Perlin } from '@/util/noise';
import { useRafFn, useResizeObserver } from '@vueuse/core';
import { onMounted, reactive, shallowRef, useTemplateRef } from 'vue';

type Point = {
	coords: { x: number, y: number }
	noisy?: boolean
} | {
	between: { 
		/** Point ID */
		a: number 
		/** Point ID */
		b: number
		/** Delta between points A and B */
		d: number 
	},
	noisy?: boolean
}

type Line = {
	a: number
	b: number
	color: string
}

type PointInstance = Point & { id: number }

const POINT_RADIUS = 5;


export default /*@__PURE__*/_defineComponent({
  __name: 'CoolBackground',
  setup(__props, { expose: __expose }) {
  __expose();

function rewritePoints(points: PointInstance[]): Point[] {
	const newPoints: Point[] = [];

	for(const point of points) {
		newPoints[point.id] = point;
	}

	return newPoints;
}

const xPerlin = shallowRef(new Perlin(Math.random()));
const yPerlin = shallowRef(new Perlin(Math.random()));

const pointData = rewritePoints([
	{ id: 1 , coords:  { x: 0.25, y: 0.4 }, noisy: true },
	{ id: 2 , coords:  { x: 0.5, y: -0.1 }, noisy: true },
	{ id: 3 , between: { a: 1, b: 2, d: 0.5 }, noisy: true },
	{ id: 4 , coords:  { x: -0.1, y: 0.9 }, noisy: true },
	{ id: 5 , coords:  { x: 0.3, y: -0.1 }, noisy: true },
	{ id: 6 , between: { a: 1, b: 5, d: 0.3 }, noisy: true },
	{ id: 7 , between: { a: 1, b: 5, d: 0.6 }, noisy: true },
	{ id: 8 , coords:  { x: -0.1, y: 0.4 }, noisy: true },
	{ id: 9 , coords:  { x: 0.3, y: 1.1 }, noisy: true },
	{ id: 10, between: { a: 1, b: 4, d: 0.4 }},
	{ id: 11, between: { a: 3, b: 9, d: 0.5 }},
	{ id: 12, between: { a: 3, b: 9, d: 0.8 }, noisy: true },
	{ id: 13, coords:  { x: 0.8, y: 1.1 }}
]);

const lineData: Line[] = [
	{ a: 1, b: 2, color: "#435FEE" },
	{ a: 1, b: 4, color: "#435FEE" },
	{ a: 1, b: 5, color: "#FAAD23" },
	{ a: 3, b: 6, color: "#ED256A" },
	{ a: 1, b: 8, color: "#16B4C3" },
	{ a: 8, b: 7, color: "#1DAA89" },
	{ a: 3, b: 9, color: "#ED256A" },
	{ a: 1, b: 11, color: "#FAAD23" },
	{ a: 11, b: 10, color: "#1DAA89" },
	{ a: 10, b: 12, color: "#1DAA89" },
	{ a: 11, b: 13, color: "#16B4C3" }
];

const myCanvas = useTemplateRef("coolCanvas");
const canvasSize = reactive({
	width: 0, height: 0
});

useResizeObserver(myCanvas, (entries) => {
	const { width, height } = entries[0].contentRect;
	canvasSize.width = width;
	canvasSize.height = height;
});

onMounted(() => {
	const rect = myCanvas.value?.getBoundingClientRect();
	if(!rect) return;
	canvasSize.width = rect.width;
	canvasSize.height = rect.height;
});

function lerp(a: number, b: number, alpha: number) {
	return a + (alpha * (b - a));
}

function lerp2d(a: {x: number, y: number}, b: {x: number, y: number}, alpha: number) {
	return {x: lerp(a.x, b.x, alpha), y: lerp(a.y, b.y, alpha)};
}

const { pause, resume } = useRafFn(({ delta, timestamp }) => {
	if(!myCanvas.value) return;
	myCanvas.value.width = canvasSize.width;
	myCanvas.value.height = canvasSize.height;

	const ctx = myCanvas.value?.getContext("2d");
	if (!ctx) return;
	const {width, height} = ctx.canvas;

	const scaler = 0.1 * (1000 / width);
	const timestampRescale = timestamp / (1000 * 3 * 3);

	ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
	ctx.lineWidth = 2;

	// ctx.fillStyle = "black";
	// ctx.fillText(xPerlin.value.perlin2(timestampRescale, 0).toString() + "\n" + timestampRescale, 10, 10);

	function derivePoint(point: Point): {x: number, y: number} {
		if("coords" in point) {
			if(point.noisy) {
				const {x, y} = point.coords;
				
				const x1 = x + (scaler * xPerlin.value.perlin2(x + timestampRescale, y));
				const y1 = y + (scaler * yPerlin.value.perlin2(x + timestampRescale, y));

				return { x: x1 * width, y: y1 * height };
			} else {
				return { x: point.coords.x * width, y: point.coords.y * height };
			}
		} else {
			const pointA = derivePoint(pointData[point.between.a]);
			const pointB = derivePoint(pointData[point.between.b]);

			if(point.noisy) {
				const newDelta = point.between.d + (xPerlin.value.perlin2(timestampRescale, point.between.d * 10 * (point.between.a ** point.between.b)) * 0.1);

				return lerp2d(pointA, pointB, newDelta);
			} else {
				return lerp2d(pointA, pointB, point.between.d);
			}

		}
	}

	ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
	ctx.save();

	for(const line of lineData) {
		const [s, e] = [derivePoint(pointData[line.a]), derivePoint(pointData[line.b])];
		ctx.beginPath();
		ctx.moveTo(s.x, s.y);
		ctx.lineTo(e.x, e.y);
		ctx.strokeStyle = line.color;
		ctx.stroke();
		ctx.closePath();
	}

	for(const point of pointData) {
		if(!point) continue;
		const p = derivePoint(point);
		ctx.beginPath();
		ctx.arc(p.x, p.y, POINT_RADIUS, 0, 2 * Math.PI);
		ctx.fillStyle = "#D9D9D9";
		ctx.fill();
		ctx.closePath();
	}

	ctx.restore();
});


const __returned__ = { rewritePoints, xPerlin, yPerlin, pointData, lineData, myCanvas, canvasSize, lerp, lerp2d, POINT_RADIUS, pause, resume, get Perlin() { return Perlin }, get useRafFn() { return useRafFn }, get useResizeObserver() { return useResizeObserver }, onMounted, reactive, shallowRef, useTemplateRef }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})